import React from 'react';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Link from 'components/link';
import { generateNeighbourhoodSlug } from 'utils/generate-neighbourhood-slug';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useEaseOutProgress } from 'hooks/use-ease-out-progress';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';

import type { TopNeighbourhood } from '@zoocasa/go-search';

export interface TopNeighbourhoodsBarsProps {
  topNeighbourhoods: readonly TopNeighbourhood[];
  /** Cap on the number of rows rendered, ranked by listing count. */
  limit?: number;
  /** City's own info-page slug (e.g. "calgary-ab") — rows link to `/{citySlug}-info/{neighbourhood-slug}` when set. */
  citySlug?: string;
}

/**
 * Horizontal bars for the top neighbourhoods by listing count, ranked
 * highest first, bar width proportional to the top neighbourhood's count.
 * Each row fades slightly more than the last, matching the design. Links to
 * the neighbourhood's own info page when a city slug is available — the
 * API gives us name+count only, so the neighbourhood slug is derived.
 */
const TopNeighbourhoodsBars = ({ topNeighbourhoods, limit = 5, citySlug }: TopNeighbourhoodsBarsProps) => {
  const { formatNumber } = useI18nFormatting();
  // Bars fill in from 0 on mount, same ease-out curve as the gauge.
  const progress = useEaseOutProgress([topNeighbourhoods]);

  const rows = (topNeighbourhoods || [])
    .filter(n => n?.name && n.count > 0)
    .slice()
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);

  if (rows.length === 0) {
    return null;
  }

  const maxCount = rows[0].count;

  return (
    <div className={styles.component}>
      {rows.map((row, index) => {
        const rowContent = (
          <>
            <div className={styles.rowContent}>
              <div className={styles.rowHeader}>
                <span className={styles.label}>{row.name}</span>
                <span className={styles.meta}>{formatNumber(row.count)}</span>
              </div>
              <div className={styles.track}>
                <div className={styles.fill} style={{ width: `${(row.count / maxCount) * 100 * progress}%`, opacity: 1 - index * 0.13 }} />
              </div>
            </div>
            <ArrowForward className={styles.arrow} fontSize="small" />
          </>
        );
        return citySlug ? (
          <Link key={row.name} href={`/${citySlug}-info/${generateNeighbourhoodSlug(row.name)}`} className={buildClassName(styles.row, styles.rowLink)}>
            {rowContent}
          </Link>
        ) : (
          <div key={row.name} className={styles.row}>
            {rowContent}
          </div>
        );
      })}
    </div>
  );
};

export default TopNeighbourhoodsBars;
